import { useState } from "react";
import { Trash2 } from "lucide-react";
import { ApiError } from "@/api/client";
import { useForgetRunner, useRunners } from "@/api/suites";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";

function ago(iso: string | null): string {
  if (!iso) return "never";
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export function RunnersListPage() {
  const runners = useRunners();
  const forget = useForgetRunner();
  const permissions = usePermissions();
  const [error, setError] = useState<string | null>(null);

  const rows = runners.data ?? [];
  const remotes = rows.filter((r) => !r.is_local);

  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <header>
        <h2 className="text-2xl font-semibold text-foreground">Runners</h2>
        <p className="text-sm text-muted-foreground">
          Where suites can send work. Runners register themselves — they call
          home, collect assignments, and run them against their own backends.
          There is nothing to configure here.
        </p>
      </header>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="border border-border rounded-lg divide-y divide-border bg-card">
        {rows.map((r) => (
          <div key={r.id} className="flex items-start gap-3 px-4 py-3">
            <span
              className={cn("mt-1.5 w-2 h-2 rounded-full flex-shrink-0",
                r.online ? "bg-emerald-500" : "bg-muted-foreground/40")}
              title={r.online ? "checked in recently" : "not checked in recently"}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-foreground">{r.name}</span>
                {r.is_local && (
                  <span className="rounded border border-border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    this runner
                  </span>
                )}
                {r.cloud && (
                  <span className="rounded border border-sky-500/50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-500">
                    {r.cloud}{r.region ? ` · ${r.region}` : ""}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground font-mono">{r.id}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {r.backend_count} backend{r.backend_count === 1 ? "" : "s"}
                {!r.is_local && ` · ${r.scenario_count} scenarios`}
                {!r.is_local && ` · last check-in ${ago(r.last_seen_at)}`}
                {r.pending_assignments > 0 && (
                  <span className="text-amber-500"> · {r.pending_assignments} queued</span>
                )}
                {r.agent_version && ` · agent ${r.agent_version}`}
              </p>
            </div>
            {!r.is_local && (
              <button
                type="button"
                disabled={!permissions.canMutate}
                title="Forget this runner. A live one re-registers on its next check-in."
                onClick={async () => {
                  if (!confirm(`Forget ${r.name}? A live runner will re-register itself.`)) return;
                  setError(null);
                  try { await forget.mutateAsync(r.id); } catch (e) {
                    setError(e instanceof ApiError ? e.message : String(e));
                  }
                }}
                className="text-muted-foreground hover:text-destructive disabled:opacity-30"
                aria-label={`Forget ${r.name}`}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      {remotes.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No remote runners have checked in. One appears here on its first
          call home, once it is deployed with this runner's URL and the shared
          agent token.
        </p>
      )}
    </div>
  );
}
